import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Check, CreditCard, ArrowRight, ArrowLeft, PenTool, Award, AlertCircle, ShieldCheck, PenLine } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { money, formatApiErrorDetail } from "../lib/helpers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { US_STATES, PROMO_CODES } from "../lib/constants";
import Seo from "../components/Seo";

const AUTH_PRICE = 1000;
const GRADE_PRICE = 1000;
const DRAFT_KEY = "mas_submit_draft";

const YEARS = ["Unknown", ...Array.from({ length: 2026 - 1900 + 1 }, (_, i) => String(2026 - i))];

const CARD_FIELDS = [
  ["subject", "Subject / Player *", "e.g. Michael Jordan"],
  ["brand", "Brand / Set *", "e.g. Topps Chrome"],
  ["cardnum", "Card # *", "e.g. 23"],
  ["category", "Category", "e.g. Basketball"],
  ["variant", "Variant", "e.g. Holo"],
];

const emptyCard = () => ({ subject: "", year: "", brand: "", cardnum: "", category: "", variant: "", declared_value: "", autograph_auth: false, autograph_grade: false });

const REQUIRED_CARD = ["subject", "year", "brand", "cardnum", "declared_value"];
const isBlank = (v) => String(v ?? "").trim() === "";
const cardFieldError = (c, f) => (f === "declared_value" ? isBlank(c[f]) || Number(c[f]) <= 0 : isBlank(c[f]));
const cardValid = (c) => REQUIRED_CARD.every((f) => !cardFieldError(c, f));
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEPS = ["Service", "Cards", "Shipping", "Review", "Pay"];

const loadDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || {}; } catch { return {}; } };

export default function Submit() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");
  const d0 = editId ? {} : loadDraft();
  const [tiers, setTiers] = useState([]);
  const [step, setStep] = useState(d0.step || 1);
  const [tier, setTier] = useState(d0.tier || null);
  const [cards, setCards] = useState(d0.cards?.length ? d0.cards : [emptyCard()]);
  const [returnName, setReturnName] = useState(d0.returnName || "");
  const [email, setEmail] = useState(d0.email || "");
  const [street, setStreet] = useState(d0.street || "");
  const [city, setCity] = useState(d0.city || "");
  const [stateReg, setStateReg] = useState(d0.stateReg || "");
  const [zip, setZip] = useState(d0.zip || "");
  const [country, setCountry] = useState(d0.country || "US");
  const [notes, setNotes] = useState(d0.notes || "");
  const [promo, setPromo] = useState(d0.promo || "");
  const [appliedPromo, setAppliedPromo] = useState(d0.appliedPromo || "");
  const [signatureRequired, setSignatureRequired] = useState(d0.signatureRequired || false);
  const [err2, setErr2] = useState(false);
  const [err3, setErr3] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/tiers").then((r) => setTiers(r.data.tiers)); }, []);
  useEffect(() => { if (user?.email) setEmail((e) => e || user.email); }, [user]);
  useEffect(() => {
    if (!editId) return;
    api.get(`/submissions/${editId}`).then(({ data }) => {
      if (data.status !== "pending_payment" || data.payment_status === "paid") {
        toast.error("This order is already paid and can no longer be edited.");
        nav("/portal");
        return;
      }
      setTier(data.tier);
      setCards((data.cards || []).map((c) => ({
        subject: c.subject || "", year: c.year || "", brand: c.brand || "", cardnum: c.cardnum || "",
        category: c.category || "", variant: c.variant || "",
        declared_value: c.declared_value ? String(c.declared_value) : "",
        autograph_auth: !!c.autograph_auth, autograph_grade: !!c.autograph_grade,
      })));
      setReturnName(data.return_name || "");
      setEmail(data.email || "");
      setStreet(data.street || "");
      setCity(data.city || "");
      setStateReg(data.state || "");
      setZip(data.zip_code || "");
      setCountry(data.country || "US");
      setNotes(data.notes || "");
      setSignatureRequired(!!data.signature_required);
      if (data.promo_code) { setPromo(data.promo_code); setAppliedPromo(data.promo_code); }
      setStep(4);
    }).catch(() => { toast.error("Could not load that order for editing."); nav("/portal"); });
    /* eslint-disable-next-line */
  }, [editId]);
  useEffect(() => {
    if (editId) return;
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ tier, cards, returnName, email, street, city, stateReg, zip, country, notes, promo, appliedPromo, signatureRequired, step }));
  }, [editId, tier, cards, returnName, email, street, city, stateReg, zip, country, notes, promo, appliedPromo, signatureRequired, step]);

  const selectedTier = tiers.find((t) => t.id === tier);
  const authCount = cards.filter((c) => c.autograph_auth).length;
  const gradeCount = cards.filter((c) => c.autograph_grade).length;
  const base = selectedTier ? selectedTier.amount * cards.length : 0;
  const addonTotal = authCount * AUTH_PRICE + gradeCount * GRADE_PRICE;
  const subtotal = base + addonTotal;
  const discountPct = PROMO_CODES[appliedPromo] || 0;
  const discount = Math.round(subtotal * discountPct / 100);
  const total = subtotal - discount;

  const tierMax = selectedTier ? selectedTier.max_value : null;
  const exceedsMax = (c) => tierMax != null && Number(c.declared_value) > tierMax;
  const overLimit = cards.some(exceedsMax);
  const suggestTier = (v) => {
    const fit = [...tiers].sort((a, b) => a.amount - b.amount).find((t) => t.max_value == null || Number(v) <= t.max_value);
    return fit ? fit.name : null;
  };

  const applyPromo = () => {
    const code = promo.trim().toUpperCase();
    if (PROMO_CODES[code]) { setAppliedPromo(code); toast.success(`Promo ${code} applied — ${PROMO_CODES[code]}% off!`); }
    else { setAppliedPromo(""); toast.error("Invalid promo code."); }
  };

  const cardsValid = cards.every(cardValid);
  const emailValid = emailRe.test(email.trim());
  const shippingValid = !isBlank(returnName) && emailValid && !isBlank(street) && !isBlank(city) && !isBlank(stateReg) && !isBlank(zip);

  const updateCard = (i, field, val) => setCards((c) => c.map((x, idx) => (idx === i ? { ...x, [field]: val } : x)));
  const addCard = () => setCards((c) => [...c, emptyCard()]);
  const removeCard = (i) => setCards((c) => c.filter((_, idx) => idx !== i));
  const setAuth = (i, val) => setCards((cs) => cs.map((x, idx) => (idx === i ? { ...x, autograph_auth: val, autograph_grade: val ? x.autograph_grade : false } : x)));
  const setGrade = (i, val) => setCards((cs) => cs.map((x, idx) => (idx === i ? { ...x, autograph_grade: val } : x)));

  const goCards = () => {
    if (!cardsValid) { setErr2(true); toast.error("Please complete all required fields (*) for each card."); return; }
    if (overLimit) { setErr2(true); toast.error(`One or more cards exceed the ${selectedTier.name} max declared value of ${money(tierMax * 100)}. Choose a higher service tier.`); return; }
    setStep(3);
  };
  const goReview = () => { if (!shippingValid) { setErr3(true); toast.error(emailValid ? "Please complete name, address, city, state and ZIP." : "A valid email address is required."); return; } setStep(4); };

  const goPay = async () => {
    if (!user) { toast.info("Please sign in to complete your submission — we've saved your order."); nav("/login?next=/submit"); return; }
    if (!cardsValid) { setStep(2); setErr2(true); return; }
    if (overLimit) { setStep(2); setErr2(true); toast.error(`One or more cards exceed the ${selectedTier.name} max declared value. Choose a higher service tier.`); return; }
    if (!shippingValid) { setStep(3); setErr3(true); return; }
    setSubmitting(true);
    try {
      const payload = {
        tier,
        cards: cards.map((c) => ({ ...c, declared_value: parseFloat(c.declared_value) || 0 })),
        return_name: returnName, email, street, city, state: stateReg, zip_code: zip, country, notes, promo_code: appliedPromo, signature_required: signatureRequired,
      };
      let subId = editId;
      if (editId) {
        await api.put(`/submissions/${editId}`, payload);
      } else {
        const { data: sub } = await api.post("/submissions", payload);
        subId = sub.submission_id;
      }
      const { data: co } = await api.post("/payments/checkout", { submission_id: subId, origin_url: window.location.origin });
      if (!editId) localStorage.removeItem(DRAFT_KEY);
      window.location.href = co.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
      setSubmitting(false);
    }
  };

  const Stepper = () => (
    <div className="flex flex-wrap items-center gap-2 mb-12">
      {STEPS.map((label, i) => {
        const n = i + 1;
        return (
          <div key={label} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${step === n ? "bg-[#0A0A0A] text-white" : step > n ? "text-[#0A0A0A]" : "text-slate-400"}`}>
              {step > n ? <Check size={13} /> : <span className="font-display">{n}</span>} {label}
            </div>
            {i < STEPS.length - 1 && <div className="w-5 h-px bg-slate-200" />}
          </div>
        );
      })}
    </div>
  );

  const input = "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-[#0A0A0A] focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/30 outline-none transition-all duration-200";
  const inputErr = "border-red-400 focus:border-red-500 focus:ring-red-500/30";
  const primary = "inline-flex items-center gap-2 px-8 py-4 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-[#171717] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-40 disabled:translate-y-0";
  const back = "inline-flex items-center gap-2 px-7 py-4 border-2 border-slate-200 rounded-full text-[#0A0A0A] font-bold hover:border-[#0A0A0A] transition-colors duration-300";

  const cardLabel = (c) => [c.year && c.year !== "Unknown" ? c.year : "", c.brand, c.subject].filter(Boolean).join(" ") || "Untitled card";

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16 md:py-20">
      <Seo title="Get Your Trading Cards Graded — Prices & Turnaround" description="Submit your trading cards for professional grading. Compare grading service levels, prices, and turnaround times, declare value, add autograph authentication, and check out securely." path="/submit" />
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]">{editId ? "Edit Submission" : "Grading Submission"}</span>
      <h1 className="font-display font-extrabold text-5xl md:text-6xl tracking-tighter mt-4 mb-10 text-[#0A0A0A]">{editId ? "Edit Your Order" : "Submit Your Cards"}</h1>
      <Stepper />

      {/* STEP 1 — SERVICE */}
      {step === 1 && (
        <div>
          <div className="grid gap-4 md:grid-cols-2">
            {tiers.map((t) => (
              <button key={t.id} data-testid={`tier-${t.id}`} onClick={() => setTier(t.id)}
                className={`text-left rounded-3xl border-2 p-7 transition-all duration-300 ${tier === t.id ? "border-[#0A0A0A] bg-[#0A0A0A]/5 shadow-[0_20px_40px_-20px_rgba(77,184,232,0.5)]" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <div className="flex justify-between items-start">
                  <h3 className="font-display font-bold text-2xl text-[#0A0A0A]">{t.name}</h3>
                  {tier === t.id && <span className="w-7 h-7 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center"><Check size={16} /></span>}
                </div>
                <div className="font-display font-extrabold text-4xl text-[#0A0A0A] mt-3">{money(t.amount)}<span className="text-sm text-slate-400 ml-1 font-body font-medium">/ card</span></div>
                <p className="text-slate-600 text-sm mt-4 font-semibold">{t.turnaround}</p>
                <p className="text-slate-400 text-xs mt-1">Max declared value: {t.max_value ? money(t.max_value * 100) : "Unlimited"}</p>
              </button>
            ))}
          </div>
          <div className="flex justify-end mt-10">
            <button data-testid="step1-next" disabled={!tier} onClick={() => setStep(2)} className={primary}>Continue <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {/* STEP 2 — CARDS */}
      {step === 2 && (
        <div>
          <div className="rounded-2xl border border-[#0A0A0A]/15 bg-[#FAFAFA] p-5 mb-5 flex gap-3" data-testid="declared-value-note">
            <ShieldCheck size={20} className="text-[#0A0A0A] shrink-0 mt-0.5" />
            <div className="text-sm text-slate-600 leading-relaxed">
              <strong className="text-[#0A0A0A]">About Declared Value:</strong> the amount you enter is the value MAS will
              <strong className="text-[#0A0A0A]"> declare and insure</strong> your card for on its return shipment back to you.
              MAS will insure your graded card up to this declared value — <strong className="text-[#0A0A0A]">regardless of the final grade</strong>.
              Enter each card's fair market value so it's protected in transit.
            </div>
          </div>
          <div className="space-y-4">
            {cards.map((c, i) => {
              const invalid = err2 && !cardValid(c);
              return (
                <div key={i} className={`rounded-3xl border bg-white p-6 shadow-sm ${invalid ? "border-red-300" : "border-slate-200"}`} data-testid={`card-row-${i}`}>
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-display font-bold text-lg text-[#0A0A0A]">Card {i + 1}</span>
                    {cards.length > 1 && (<button onClick={() => removeCard(i)} data-testid={`remove-card-${i}`} className="text-slate-400 hover:text-red-500 transition-colors duration-300"><Trash2 size={16} /></button>)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {/* Year dropdown */}
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Year *</label>
                      <Select value={c.year} onValueChange={(v) => updateCard(i, "year", v)}>
                        <SelectTrigger data-testid={`card-${i}-year`} className={`mt-1 bg-white rounded-xl h-[42px] text-sm ${err2 && cardFieldError(c, "year") ? "border-red-400" : "border-slate-200"}`}>
                          <SelectValue placeholder="Select year" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 text-[#0A0A0A] max-h-72">
                          {YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {CARD_FIELDS.map(([f, label, ph]) => (
                      <div key={f}>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</label>
                        <input data-testid={`card-${i}-${f}`} value={c[f]} placeholder={ph} onChange={(e) => updateCard(i, f, e.target.value)}
                          className={`mt-1 ${input} py-2.5 text-sm ${err2 && REQUIRED_CARD.includes(f) && cardFieldError(c, f) ? inputErr : ""}`} />
                      </div>
                    ))}
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Declared Value ($) *</label>
                      <input data-testid={`card-${i}-value`} type="number" min="0" value={c.declared_value} placeholder="e.g. 1000" onChange={(e) => updateCard(i, "declared_value", e.target.value)}
                        className={`mt-1 ${input} py-2.5 text-sm ${err2 && cardFieldError(c, "declared_value") ? inputErr : exceedsMax(c) ? "border-amber-400" : ""}`} />
                    </div>
                  </div>
                  {invalid && (
                    <div className="flex items-center gap-2 mt-3 text-xs font-semibold text-red-600" data-testid={`card-${i}-error`}>
                      <AlertCircle size={14} /> Please complete all required fields marked *.
                    </div>
                  )}
                  {exceedsMax(c) && (
                    <div className="flex items-start gap-2 mt-3 text-xs font-semibold text-amber-600" data-testid={`card-${i}-maxvalue-error`}>
                      <AlertCircle size={14} className="mt-0.5 shrink-0" />
                      <span>Declared value exceeds the {selectedTier.name} limit of {money(tierMax * 100)}.{suggestTier(c.declared_value) && suggestTier(c.declared_value) !== selectedTier.name ? ` Upgrade to ${suggestTier(c.declared_value)} to cover this card.` : ""}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button data-testid="add-card" onClick={addCard} className="mt-4 inline-flex items-center gap-2 font-bold text-[#0A0A0A] hover:text-[#171717] transition-colors duration-300"><Plus size={16} /> Add another card</button>
          <div className="flex justify-between mt-10">
            <button onClick={() => setStep(1)} className={back}><ArrowLeft size={16} /> Back</button>
            <button data-testid="step2-next" onClick={goCards} className={primary}>Continue <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {/* STEP 3 — SHIPPING */}
      {step === 3 && (
        <div className="max-w-2xl">
          <div className="space-y-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Return Name *</label>
              <input data-testid="return-name" value={returnName} onChange={(e) => setReturnName(e.target.value)} className={`mt-2 ${input} ${err3 && isBlank(returnName) ? inputErr : ""}`} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Email Address *</label>
              <input data-testid="return-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`mt-2 ${input} ${err3 && !emailValid ? inputErr : ""}`} />
              {err3 && !emailValid && <p className="text-xs font-semibold text-red-600 mt-1.5">Enter a valid email address.</p>}
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Street Address *</label>
              <input data-testid="ship-street" value={street} placeholder="123 Main St" onChange={(e) => setStreet(e.target.value)} className={`mt-2 ${input} ${err3 && isBlank(street) ? inputErr : ""}`} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">City *</label>
                <input data-testid="ship-city" value={city} onChange={(e) => setCity(e.target.value)} className={`mt-2 ${input} ${err3 && isBlank(city) ? inputErr : ""}`} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">State / Province *</label>
                <Select value={stateReg} onValueChange={setStateReg}>
                  <SelectTrigger data-testid="ship-state" className={`mt-2 bg-white rounded-xl ${err3 && isBlank(stateReg) ? "border-red-400" : "border-slate-200"}`}>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 text-[#0A0A0A] max-h-72">
                    {US_STATES.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">ZIP / Postal Code *</label>
                <input data-testid="ship-zip" value={zip} onChange={(e) => setZip(e.target.value)} className={`mt-2 ${input} ${err3 && isBlank(zip) ? inputErr : ""}`} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Country</label>
                <input data-testid="ship-country" value={country} onChange={(e) => setCountry(e.target.value)} className={`mt-2 ${input}`} />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Notes (optional)</label>
              <textarea data-testid="notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={`mt-2 ${input}`} />
            </div>
            <button type="button" data-testid="signature-toggle" onClick={() => setSignatureRequired((v) => !v)}
              className={`w-full flex items-start gap-3 text-left rounded-2xl border-2 p-4 transition-all duration-300 ${signatureRequired ? "border-[#0A0A0A] bg-[#0A0A0A]/5" : "border-slate-200 hover:border-slate-300"}`}>
              <span className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${signatureRequired ? "bg-[#0A0A0A] text-white" : "border-2 border-slate-300"}`}>{signatureRequired && <Check size={13} />}</span>
              <span>
                <span className="flex items-center gap-1.5 font-bold text-[#0A0A0A] text-sm"><PenLine size={14} /> Require signature on delivery</span>
                <span className="block text-xs text-slate-500 mt-0.5">Recommended for high-value packages — the carrier will require a signature when your graded cards are delivered back to you.</span>
              </span>
            </button>
          </div>
          <div className="flex justify-between mt-10">
            <button onClick={() => setStep(2)} className={back}><ArrowLeft size={16} /> Back</button>
            <button data-testid="step3-next" onClick={goReview} className={primary}>Review <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {/* STEP 4 — REVIEW & ADD-ONS */}
      {step === 4 && (
        <div>
          <h3 className="font-display font-bold text-2xl text-[#0A0A0A] mb-2">Review your cards</h3>
          <p className="text-slate-500 mb-6 max-w-2xl">Add optional autograph services per card. <strong className="text-[#0A0A0A]">Auto Grade</strong> requires <strong className="text-[#0A0A0A]">Autograph Authentication</strong> to be selected on that card.</p>
          <div className="space-y-4">
            {cards.map((c, i) => (
              <div key={i} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`review-card-${i}`}>
                <div className="flex flex-wrap justify-between items-start gap-3">
                  <div>
                    <div className="font-display font-bold text-lg text-[#0A0A0A]">{cardLabel(c)}</div>
                    <div className="text-sm text-slate-500 mt-0.5">
                      {[c.cardnum && `#${c.cardnum}`, c.category, c.variant].filter(Boolean).join(" · ") || "—"}
                      {c.declared_value ? ` · Declared ${money(Math.round(Number(c.declared_value) * 100))}` : ""}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-[#0A0A0A]">{selectedTier ? money(selectedTier.amount) : ""}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  <button type="button" data-testid={`card-${i}-autograph-auth`} onClick={() => setAuth(i, !c.autograph_auth)}
                    className={`flex items-start gap-3 text-left rounded-2xl border-2 p-4 transition-all duration-300 ${c.autograph_auth ? "border-[#0A0A0A] bg-[#0A0A0A]/5" : "border-slate-200 hover:border-slate-300"}`}>
                    <span className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${c.autograph_auth ? "bg-[#0A0A0A] text-white" : "border-2 border-slate-300"}`}>{c.autograph_auth && <Check size={13} />}</span>
                    <span>
                      <span className="flex items-center gap-1.5 font-bold text-[#0A0A0A] text-sm"><PenTool size={14} /> Authenticate an Autograph</span>
                      <span className="block text-xs text-slate-500 mt-0.5">Verify the signature on this card. <strong className="text-[#0A0A0A]">+{money(AUTH_PRICE)}</strong></span>
                    </span>
                  </button>
                  <button type="button" data-testid={`card-${i}-autograph-grade`} disabled={!c.autograph_auth} onClick={() => c.autograph_auth && setGrade(i, !c.autograph_grade)}
                    className={`flex items-start gap-3 text-left rounded-2xl border-2 p-4 transition-all duration-300 ${!c.autograph_auth ? "border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed" : c.autograph_grade ? "border-[#0A0A0A] bg-[#0A0A0A]/5" : "border-slate-200 hover:border-slate-300"}`}>
                    <span className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${c.autograph_grade ? "bg-[#0A0A0A] text-white" : "border-2 border-slate-300"}`}>{c.autograph_grade && <Check size={13} />}</span>
                    <span>
                      <span className="flex items-center gap-1.5 font-bold text-[#0A0A0A] text-sm"><Award size={14} /> Auto Grade</span>
                      <span className="block text-xs text-slate-500 mt-0.5">
                        {c.autograph_auth ? <>Grade the autograph itself. <strong className="text-[#0A0A0A]">+{money(GRADE_PRICE)}</strong></> : "Requires Autograph Authentication first."}
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-10">
            <button onClick={() => setStep(3)} className={back}><ArrowLeft size={16} /> Back</button>
            <button data-testid="step4-next" onClick={() => setStep(5)} className={primary}>Continue to Pay <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {/* STEP 5 — PAY */}
      {step === 5 && (
        <div className="max-w-2xl">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <h3 className="font-display font-bold text-2xl text-[#0A0A0A] mb-5">Order Summary</h3>
            {[["Service Tier", selectedTier?.name], ["Turnaround", selectedTier?.turnaround], ["Cards", cards.length], ["Price per card", selectedTier && money(selectedTier.amount)]].map(([k, v]) => (
              <div key={k} className="flex justify-between py-3 border-b border-slate-100"><span className="text-slate-500">{k}</span><span className="font-semibold text-[#0A0A0A]">{v}</span></div>
            ))}
            <div className="flex justify-between py-3 border-b border-slate-100"><span className="text-slate-500">Grading subtotal</span><span className="font-semibold text-[#0A0A0A]">{money(base)}</span></div>
            <div className="flex justify-between py-3 border-b border-slate-100" data-testid="summary-insured"><span className="text-slate-500">Insured return value</span><span className="font-semibold text-[#0A0A0A]">{money(Math.round(cards.reduce((s, c) => s + (parseFloat(c.declared_value) || 0), 0) * 100))}</span></div>
            <div className="flex justify-between py-3 border-b border-slate-100" data-testid="summary-signature"><span className="text-slate-500">Signature on delivery</span><span className="font-semibold text-[#0A0A0A]">{signatureRequired ? "Required" : "Not required"}</span></div>
            {authCount > 0 && (<div className="flex justify-between py-3 border-b border-slate-100" data-testid="summary-auth"><span className="text-slate-500">Autograph Authentication × {authCount}</span><span className="font-semibold text-[#0A0A0A]">{money(authCount * AUTH_PRICE)}</span></div>)}
            {gradeCount > 0 && (<div className="flex justify-between py-3 border-b border-slate-100" data-testid="summary-grade"><span className="text-slate-500">Autograph Grading × {gradeCount}</span><span className="font-semibold text-[#0A0A0A]">{money(gradeCount * GRADE_PRICE)}</span></div>)}
            {discount > 0 && (<div className="flex justify-between py-3 border-b border-slate-100" data-testid="summary-discount"><span className="text-emerald-700 font-semibold">Promo {appliedPromo} (−{discountPct}%)</span><span className="font-semibold text-emerald-700">−{money(discount)}</span></div>)}
            <div className="flex items-center gap-2 py-4 border-b border-slate-100">
              <input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Promo code (e.g. GRADE10)" className="flex-1 bg-white border border-slate-200 rounded-xl px-4 h-11 text-sm uppercase outline-none focus:border-[#0A0A0A]" />
              <button data-testid="apply-promo" onClick={applyPromo} className="px-5 h-11 border-2 border-[#0A0A0A] rounded-xl text-sm font-bold text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors">Apply</button>
            </div>
            <div className="flex justify-between items-center py-5"><span className="font-display font-bold text-xl text-[#0A0A0A]">Total</span><span className="font-display font-extrabold text-3xl text-[#0A0A0A]" data-testid="order-total">{money(total)}</span></div>
            <p className="text-xs text-slate-400">Applicable tax is calculated securely at checkout by Stripe.</p>
          </div>
          <div className="flex justify-between mt-10">
            <button onClick={() => setStep(4)} className={back}><ArrowLeft size={16} /> Back</button>
            <button data-testid="checkout-button" onClick={goPay} disabled={submitting} className={primary}><CreditCard size={18} /> {submitting ? "Redirecting…" : `Pay ${money(total)}`}</button>
          </div>
        </div>
      )}
    </div>
  );
}
